import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { getSemanticRanking, getJdMatchingStatus } from "../../../../api/client";
import {
    ArrowLeft,
    User,
    Award,
    AlertCircle,
    RefreshCw,
    Download,
    CheckCircle,
    XCircle,
    UserCheck,
    ExternalLink,
    TrendingUp,
    FileText,
} from "lucide-react";
import { CircularProgress, Tooltip } from "@mui/material";
import NotificationPopup from "../../component/NotificationPopup";
import { useSkillAnalysis } from '../../../../hooks/useCandidateDetailApis';
import "../../admin.css";
import SkillAnalysisModal from "../candidate/SkillAnalysisModal";
import IndividualScheduleModal from "./MatchingCandidateInterviewScedule";

const MatchingResumes = ({ onLogout }) => {
    const { jobId, taskId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const limit = queryParams.get('limit') || 20;
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [matchData, setMatchData] = useState(null);
    const [jobTitle, setJobTitle] = useState("");
    const [jobDescription, setJobDescription] = useState(null);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // Pagination states
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // Individual scheduling modal states
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [scheduleResult, setScheduleResult] = useState(null);
    const [isCreatingInvite, setIsCreatingInvite] = useState(false);

    // Skill analysis states
    const [showSkillAnalysisModal, setShowSkillAnalysisModal] = useState(false);
    // const [selectedCandidateForAnalysis, setSelectedCandidateForAnalysis] = useState(null);
    const [skillAnalysisLoading, setSkillAnalysisLoading] = useState(false);

    // Polling control refs
    const pollTimerRef = useRef(null);
    const pollAttemptsRef = useRef(0);
    const MAX_POLL_ATTEMPTS = 12; // 12 * 5s = 60s max polling limit

    // Use skill analysis hook
    const {
        skillAnalysis,
        setSkillAnalysis,
        analyzeSkills,
        reanalyzeSkills,
        createInviteWithSkills
    } = useSkillAnalysis();

    const stopPolling = useCallback(() => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        pollAttemptsRef.current = 0;
    }, []);

    const startPolling = useCallback((pageToFetch = 1) => {
        if (pollTimerRef.current) return;

        pollAttemptsRef.current = 0;
        pollTimerRef.current = setInterval(async () => {
            pollAttemptsRef.current += 1;

            if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
                stopPolling();
                return;
            }

            try {
                // 1. Check matching status
                const statusData = await getJdMatchingStatus(jobId).catch(() => null);
                const status = statusData?.matching_status?.toLowerCase();

                // 2. Silently fetch latest ranking data
                const data = await getSemanticRanking(jobId, { limit, top_n: limit, page: pageToFetch });
                if (data) {
                    setMatchData(data);
                    if (data.pagination) {
                        setPage(data.pagination.current_page);
                        setTotalPages(data.pagination.total_pages);
                        setTotalCount(data.pagination.total_count);
                    } else {
                        setTotalCount(data.results_count || (data.results ? data.results.length : 0));
                    }
                }

                // 3. Stop polling if matching finished or failed
                if (status === 'ready' || status === 'completed') {
                    stopPolling();
                    setSnackbar({
                        open: true,
                        message: "Semantic matching completed successfully!",
                        severity: "success",
                    });
                } else if (status === 'failed') {
                    stopPolling();
                    setError("Semantic matching failed. Please try again.");
                }
            } catch (err) {
                console.error("Error during matching polling:", err);
                stopPolling();
            }
        }, 5000);
    }, [jobId, limit, stopPolling]);

    const fetchRanking = useCallback(async (pageToFetch = 1, isSilent = false) => {
        if (!isSilent) {
            setLoading(true);
            setError(null);
        }
        try {
            const data = await getSemanticRanking(jobId, { 
                limit,
                top_n: limit,
                page: pageToFetch
            });
            
            setMatchData(data);
            if (data.pagination) {
                setPage(data.pagination.current_page);
                setTotalPages(data.pagination.total_pages);
                setTotalCount(data.pagination.total_count);
            } else {
                setTotalCount(data.results_count || (data.results ? data.results.length : 0));
            }

            // Only start polling if matching is actively in progress
            const statusData = await getJdMatchingStatus(jobId).catch(() => null);
            const status = statusData?.matching_status?.toLowerCase();

            if (status === 'processing' || status === 'pending') {
                startPolling(pageToFetch);
            } else {
                stopPolling();
            }
        } catch (err) {
            console.error("Error fetching semantic ranking:", err);
            if (!isSilent) {
                setError("Failed to fetch match results. Please try again.");
            }
            stopPolling();
        } finally {
            if (!isSilent) {
                setLoading(false);
            }
        }
    }, [jobId, limit, startPolling, stopPolling]);

    // Fetch job title and description once per jobId
    useEffect(() => {
        const fetchJobTitle = async () => {
            try {
                const res = await api.get(`job-descriptions/${jobId}/`, {
                    headers: {
                        Authorization: `${localStorage.getItem("adminToken")}`,
                    },
                });
                setJobTitle(res.data.title || "Job Description");
                setJobDescription(res.data);
            } catch (err) {
                console.error("Error fetching job title:", err);
                setJobTitle("Job Description");
            }
        };

        fetchJobTitle();
    }, [jobId]);

    // Fetch rankings when jobId or page changes
    useEffect(() => {
        fetchRanking(page);
    }, [jobId, page, fetchRanking]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);


    const handleClose = () => setSnackbar((prev) => ({ ...prev, open: false }));

    const exportToCSV = () => {
        if (!matchData || !matchData.results) return;

        const headers = ["Candidate Name", "Email", "Phone Number", "Match Percentage", "Match Type", "Resume"];
        const rows = matchData.results.map((result) => [
            result.candidate_name,
            result.candidate_email || 'N/A',
            result.candidate_phone || 'N/A',
            `${result.final_score || result.match_percentage}%`,
            result.match_type || 'N/A',
            result.resume_url,
        ]);

        let csvContent = headers.join(",") + "\n";
        rows.forEach((row) => {
            csvContent += row.join(",") + "\n";
        });

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
            "download",
            `matching_resumes_${jobTitle.replace(/\s+/g, "_")}.csv`
        );
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Individual schedule modal handlers
    const handleOpenScheduleModal = (candidate) => {
        setSelectedCandidate(candidate);
        setSelectedSkills([]);
        setScheduleResult(null);
        setShowScheduleModal(true);
    };

    const handleCreateInvite = async () => {
        if (!selectedCandidate) return;

        setIsCreatingInvite(true);

        try {
            let response;

            if (selectedSkills.length > 0) {
                // Create invite with skills
                response = await createInviteWithSkills(
                    3,
                    jobId,
                    selectedSkills
                );
            } else {
                // Create invite without skills
                response = await api.post(`/admin/generate-multi-jd-invites/`, {
                    candidateId: selectedCandidate.candidate_id,
                    jobDescriptionIds: [jobId],
                    sendEmail: true
                });
            }

            setScheduleResult({
                success: true,
                candidateName: selectedCandidate.candidate_name,
                interviewUrl: response.data.successfulInvites?.[0]?.interviewUrl || response.data.interviewUrl,
                skillsUsed: selectedSkills.length > 0
            });

            setSnackbar({
                open: true,
                message: `Successfully created invite for ${selectedCandidate.candidate_name}`,
                severity: 'success'
            });

        } catch (error) {
            console.error('Error creating invite:', error);
            setScheduleResult({
                success: false,
                candidateName: selectedCandidate.candidate_name,
                error: error.response?.data?.error || 'Failed to create invite'
            });

            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to create invite. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsCreatingInvite(false);
        }
    };

    // Skill analysis handlers
    const handleSkillAnalysis = async () => {
        if (!selectedCandidate) return;

        setShowSkillAnalysisModal(true);
        setSkillAnalysisLoading(true);

        try {
            if (!selectedCandidate?.candidate_id || !jobId) {
                throw new Error('Missing candidate or job description');
            }
            const data = await analyzeSkills(selectedCandidate.candidate_id, jobId);
            setSkillAnalysis(data);
            setSnackbar({
                open: true,
                message: 'Skill analysis completed successfully!',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error analyzing skills:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to analyze skills. Please try again.',
                severity: 'error'
            });
            setShowSkillAnalysisModal(false);
        } finally {
            setSkillAnalysisLoading(false);
        }
    };

    const handleClearCachesAndRestart = async () => {
        if (!selectedCandidate?.candidate_id || !jobId) return;
        setSkillAnalysisLoading(true);
        try {
            const data = await analyzeSkills(
                selectedCandidate.candidate_id,
                jobId
            );
            setSkillAnalysis(data);
            setSnackbar({
                open: true,
                message:
                    "Caches cleared and skill analysis completed successfully!",
                severity: "success",
            });
        } catch (error) {
            console.error("Error re-analyzing after cache clear:", error);
            setSnackbar({
                open: true,
                message:
                    error.message ||
                    "Failed to analyze skills after clearing caches.",
                severity: "error",
            });
        } finally {
            setSkillAnalysisLoading(false);
        }
    };

    const handleApplySkillsAndReturn = () => {
        setShowSkillAnalysisModal(false);
        setSkillAnalysis(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div
            className="min-h-screen " style={{ paddingTop: "10px" }}>
            <NotificationPopup
                open={snackbar.open}
                onClose={handleClose}
                severity={snackbar.severity}
                message={snackbar.message}
            />

            <div className="max-w-8xl mx-auto p-3 sm:p-4 h-full">
                <div className="bg-white rounded-xl flex flex-col h-full overflow-hidden shadow-sm border border-gray-100">
                    {/* Header */}
                    <div
                        className="p-6 text-white"
                        style={{
                            background: "linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)",
                        }}
                    >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => navigate("/jobdescriptions")}
                                    className="flex items-center justify-center w-9 h-9 rounded-full bg-white/20 text-white hover:bg-white/30 transition-all duration-200"
                                    title="Back to Job Descriptions"
                                >
                                    <ArrowLeft size={20} />
                                </button>
                                <div>
                                    <h1 className="text-base font-semibold text-white mb-0.5">
                                        Matching Resumes: {jobTitle}
                                    </h1>
                                    <p className="text-sm text-blue-100 leading-snug">
                                        Identify top candidates matched against your job requirements.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-6">
                                {matchData && (
                                    <div className="text-right hidden sm:block border-r border-white/20 pr-6 mr-6">
                                        <div className="text-xs text-blue-100 uppercase tracking-wider font-medium mb-0.5">Top Matches</div>
                                        <div className="text-lg font-bold">{totalCount}</div>
                                    </div>
                                )}
                                {matchData && matchData.results && matchData.results.length > 0 && (
                                    <button
                                        onClick={exportToCSV}
                                        className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg hover:bg-white/30 transition-all border border-white/30 shadow-sm text-sm font-medium"
                                    >
                                        <Download size={16} />
                                        Export CSV
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 flex-1 overflow-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-12" style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial' }}>
                                <CircularProgress size={48} sx={{ color: "#2563eb" }} />
                                <p className="mt-4 text-gray-600 text-lg">
                                    Calculating AI Rankings...
                                </p>
                                <p className="mt-2 text-gray-500 text-sm">
                                    Our Semantic Matching Engine is analyzing candidate profiles.
                                </p>
                            </div>
                        ) : error ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                    <AlertCircle size={32} className="text-red-600" />
                                </div>
                                <p className="text-lg text-gray-900 mb-2">Error</p>
                                <p className="text-gray-600 mb-4">{error}</p>
                                <button
                                    onClick={() => {
                                        setError(null);
                                        fetchRanking();
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    <RefreshCw size={18} />
                                    Retry
                                </button>
                            </div>
                        ) : matchData ? (
                            <div className="space-y-6">
                                {/* Summary Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm hover:shadow-md transition-all">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                                                <User size={20} className="text-blue-500" />
                                            </div>
                                            <div>
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">
                                                    Total Evaluated
                                                </p>
                                                <p className="text-xl font-bold text-gray-900">
                                                    {matchData.total_resumes_evaluated || matchData.total_evaluated || 0}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm hover:shadow-md transition-all">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                                                <CheckCircle size={20} className="text-emerald-500" />
                                            </div>
                                            <div>
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">
                                                    Showing Top Matches
                                                </p>
                                                <p className="text-xl font-bold text-gray-900">
                                                    {totalCount}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>                                {/* Matching Results Table */}
                                {matchData.results && matchData.results.length > 0 ? (
                                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                        <div className="overflow-x-auto">
                                            <table className="w-full table-auto">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                            Rank
                                                        </th>
                                                        <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                            Candidate
                                                        </th>
                                                        <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                            Contact Information
                                                        </th>
                                                        <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                            Match Score
                                                        </th>
                                                        <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                            Resume
                                                        </th>
                                                        <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                            Activity
                                                        </th>
                                                        <th className="text-center py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                            Actions
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {matchData.results
                                                        .filter(result => result.is_active !== false)
                                                        .map((result, index) => {
                                                        const matchScore = parseFloat(
                                                            result.semantic_match?.final_score !== undefined ? result.semantic_match.final_score :
                                                            (result.final_score || result.match_percentage || 0)
                                                        );
                                                        const getScoreStyles = (score) => {
                                                            if (score >= 80) return "text-emerald-600 bg-emerald-50 border-emerald-100";
                                                            if (score >= 60) return "text-sky-600 bg-sky-50 border-sky-100";
                                                            if (score >= 40) return "text-orange-600 bg-orange-50 border-orange-100";
                                                            return "text-rose-600 bg-rose-50 border-rose-100";
                                                        };

                                                        const breakdown = result.semantic_match?.breakdown || result.breakdown || {};
                                                        const skillMatches = [
                                                            ...(breakdown.required_skills || []),
                                                            ...(breakdown.optional_skills || [])
                                                        ].filter(s => s.status === 'matched');
                                                        const coverage = (
                                                            result.semantic_match?.coverage !== undefined ? result.semantic_match.coverage :
                                                            (result.coverage !== undefined ? result.coverage : (breakdown.coverage || 0))
                                                        ) * 100;
                                                        const experienceFactor = breakdown.experience_factor || 0;

                                                        return (
                                                            <tr
                                                                key={result.resume_id || result.id}
                                                                className="hover:bg-gray-50/80 transition-all duration-150"
                                                            >
                                                                <td className="py-2.5 px-4 align-middle">
                                                                    <span className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-600">
                                                                        {index + 1}
                                                                    </span>
                                                                </td>
                                                                <td className="py-2.5 px-4 align-middle">
                                                                    <div 
                                                                        className="flex flex-col cursor-pointer group"
                                                                        onClick={() => navigate(`/candidates/${result.candidate_id}`)}
                                                                    >
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                                                                {result.candidate_name}
                                                                            </span>
                                                                        </div>
                                                                        <span className="text-xs text-gray-500 truncate max-w-[150px] group-hover:text-blue-500 transition-colors">
                                                                            {result.candidate_email || 'N/A'}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="py-2.5 px-4 align-middle">
                                                                    <div className="flex flex-col gap-0.5">
                                                                         <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                                                            <User size={12} className="text-gray-400" />
                                                                            <span>{result.candidate_phone || 'N/A'}</span>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="py-2.5 px-4 align-middle">
                                                                    <div className="flex items-center gap-2">
                                                                        <Tooltip
                                                                            title={
                                                                                <div className="p-3 max-w-sm space-y-4 bg-white">
                                                                                    {/* Match Breakdown Header */}
                                                                                    <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                                                                                        <div className="flex items-center gap-2">
                                                                                            <Award size={16} className="text-yellow-600" />
                                                                                            <span className="font-bold text-sm text-gray-800">Match Transparency</span>
                                                                                        </div>
                                                                                        <span className="text-xs font-bold text-blue-600">{matchScore}%</span>
                                                                                    </div>

                                                                                    {/* Coverage Progress Bar */}
                                                                                    <div className="space-y-1.5">
                                                                                        <div className="flex justify-between text-[11px]">
                                                                                            <span className="text-gray-600 font-medium">Job Requirement Coverage</span>
                                                                                            <span className="text-blue-600 font-bold">{coverage}%</span>
                                                                                        </div>
                                                                                        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                                                            <div 
                                                                                                className="h-full bg-blue-500 rounded-full transition-all duration-500" 
                                                                                                style={{ width: `${coverage}%` }}
                                                                                            />
                                                                                        </div>
                                                                                    </div>

                                                                                    {/* Experience Bonus */}
                                                                                    {experienceFactor !== 0 && (
                                                                                        <div className="flex items-center justify-between bg-emerald-50 px-2 py-1.5 rounded-lg border border-emerald-100">
                                                                                            <span className="text-[11px] text-emerald-800 font-medium">Experience Bonus</span>
                                                                                            <span className="text-[11px] text-emerald-700 font-bold">+{experienceFactor}%</span>
                                                                                        </div>
                                                                                    )}

                                                                                    {/* Skill Matches Details */}
                                                                                    <div className="space-y-2">
                                                                                        <div className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Semantic Skill Matches</div>
                                                                                        {skillMatches.length > 0 ? (
                                                                                            <div className="flex flex-wrap gap-1.5 max-h-[150px] overflow-y-auto pr-1 custom-scrollbar">
                                                                                                {skillMatches.map((detail, i) => {
                                                                                                    const similarity = (detail.similarity * 100).toFixed(0);
                                                                                                    return (
                                                                                                        <div key={i} className="flex flex-col bg-gray-50 border border-gray-100 rounded p-1.5 min-w-[80px]">
                                                                                                            <span className="text-[10px] font-bold text-gray-800 truncate" title={detail.jd_skill}>
                                                                                                                {detail.jd_skill}
                                                                                                                {detail.best_match && detail.best_match.toLowerCase() !== detail.jd_skill.toLowerCase() && (
                                                                                                                    <span className="text-[8px] font-medium text-gray-500 block">
                                                                                                                        matched as: {detail.best_match}
                                                                                                                    </span>
                                                                                                                )}
                                                                                                            </span>
                                                                                                            <div className="flex items-center justify-between mt-1">
                                                                                                                <div className="w-8 h-1 bg-gray-200 rounded-full overflow-hidden">
                                                                                                                    <div className="h-full bg-emerald-500" style={{ width: `${similarity}%` }} />
                                                                                                                </div>
                                                                                                                <span className="text-[9px] font-bold text-emerald-600">{similarity}%</span>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    );
                                                                                                })}
                                                                                            </div>
                                                                                        ) : (
                                                                                            <p className="text-[11px] italic text-gray-500">No detailed skill analysis available.</p>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            }
                                                                            slotProps={{
                                                                                tooltip: {
                                                                                    sx: {
                                                                                        bgcolor: 'white',
                                                                                        color: 'rgba(0, 0, 0, 0.87)',
                                                                                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                                                                                        border: '1px solid #e2e8f0',
                                                                                        padding: 0,
                                                                                        maxWidth: 'none',
                                                                                        '& .MuiTooltip-arrow': {
                                                                                            color: 'white',
                                                                                            '&::before': {
                                                                                                border: '1px solid #e2e8f0',
                                                                                                backgroundColor: 'white',
                                                                                            },
                                                                                        },
                                                                                    },
                                                                                },
                                                                            }}
                                                                            arrow
                                                                            placement="top"
                                                                            enterTouchDelay={0}
                                                                        >
                                                                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold border cursor-pointer transition-all duration-200 hover:shadow-sm ${getScoreStyles(matchScore)}`}>
                                                                                <TrendingUp size={12} />
                                                                                {matchScore}%
                                                                            </div>
                                                                        </Tooltip>
                                                                    </div>
                                                                </td>
                                                                <td className="py-2.5 px-4 align-middle">
                                                                    {result.resume_url ? (
                                                                        <a
                                                                            href={result.resume_url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                                                                        >
                                                                            <FileText size={16} />
                                                                            View
                                                                            <ExternalLink size={12} />
                                                                        </a>
                                                                    ) : (
                                                                        <span className="text-gray-400 text-sm">—</span>
                                                                    )}
                                                                </td>
                                                                <td className="py-2.5 px-4 align-middle">
                                                                    <div className="flex items-center">
                                                                        <span className="text-sm text-gray-600">{formatDate(result.resume_upload_date)}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="py-2.5 px-4 align-middle">
                                                                    <div className="flex justify-center">
                                                                        <button
                                                                            onClick={() => handleOpenScheduleModal(result)}
                                                                            className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-all duration-200 shadow-sm text-xs font-semibold"
                                                                        >
                                                                            <UserCheck size={14} />
                                                                            Schedule
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        
                                        {/* Pagination Controls */}
                                        {totalPages > 1 && (
                                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                                                <div className="text-sm text-gray-500">
                                                    Showing Page <span className="font-semibold text-gray-900">{page}</span> of <span className="font-semibold text-gray-900">{totalPages}</span>
                                                    <span className="ml-1">({totalCount} total candidates)</span>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                                        disabled={page === 1 || loading}
                                                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                    >
                                                        Previous
                                                    </button>
                                                    <button
                                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                                        disabled={page === totalPages || loading}
                                                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 bg-gray-50 rounded-xl">
                                        <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <User size={32} className="text-gray-400" />
                                        </div>
                                        <p className="text-lg text-gray-900 mb-2">
                                            No Matching Candidates Found
                                        </p>
                                        <p className="text-gray-600">
                                            No resumes matched the criteria for this job description
                                        </p>
                                    </div>
                                )}

                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Individual Schedule Modal */}
            <IndividualScheduleModal
                isOpen={showScheduleModal}
                onClose={() => {
                    setShowScheduleModal(false);
                    setSelectedCandidate(null);
                    setSelectedSkills([]);
                    setScheduleResult(null);
                }}
                candidate={selectedCandidate}
                jobDescription={jobDescription}
                onCreateInvite={handleCreateInvite}
                onSkillAnalysis={handleSkillAnalysis}
                selectedSkills={selectedSkills}
                setSelectedSkills={setSelectedSkills}
                scheduleResult={scheduleResult}
                isCreatingInvite={isCreatingInvite}
                setSnackbar={setSnackbar}
            />

            {/* Skill Analysis Modal */}
            <SkillAnalysisModal
                isOpen={showSkillAnalysisModal}
                onClose={handleApplySkillsAndReturn}
                selectedCandidate={selectedCandidate}
                selectedJDForAnalysis={jobDescription}
                skillAnalysis={skillAnalysis}
                skillAnalysisLoading={skillAnalysisLoading}
                selectedSkills={selectedSkills}
                setSelectedSkills={setSelectedSkills}
                onApplySkillsAndReturn={handleApplySkillsAndReturn}
                reanalyzeSkills={reanalyzeSkills}
                analysisFromMatchingResumes={true}
                onClearCachesAndRestart={handleClearCachesAndRestart}
            />
        </div>
    );
};

export default MatchingResumes;